import { IPublicClientApplication, InteractionRequiredAuthError, AccountInfo } from '@azure/msal-browser';

/** How often (ms) the periodic session check runs. Default: 5 minutes */
export const SESSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;

/** BEAD 8.11 — maximum continuous session before interactive re-authentication. */
export const DEFAULT_MAX_SESSION_HOURS = 10;

const SESSION_STARTED_AT_KEY = 'rubix_session_started_at';

/** Resolve max session duration (override via VITE_MAX_SESSION_HOURS for testing). */
export function getMaxSessionDurationMs(): number {
  const hours = Number(import.meta.env.VITE_MAX_SESSION_HOURS ?? DEFAULT_MAX_SESSION_HOURS);
  if (!Number.isFinite(hours) || hours <= 0) {
    return DEFAULT_MAX_SESSION_HOURS * 60 * 60 * 1000;
  }
  return hours * 60 * 60 * 1000;
}

/**
 * Record when the current application session began.
 * @param force When true (interactive login), always reset the clock.
 */
export function recordSessionStart(force = false): void {
  if (force || !sessionStorage.getItem(SESSION_STARTED_AT_KEY)) {
    sessionStorage.setItem(SESSION_STARTED_AT_KEY, String(Date.now()));
  }
}

export function clearSessionStart(): void {
  sessionStorage.removeItem(SESSION_STARTED_AT_KEY);
}

export function getSessionStartedAt(): number | null {
  const raw = sessionStorage.getItem(SESSION_STARTED_AT_KEY);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function isMaxSessionDurationExceeded(now = Date.now()): boolean {
  const startedAt = getSessionStartedAt();
  if (startedAt === null) return false;
  return now - startedAt > getMaxSessionDurationMs();
}

export interface SessionValidationResult {
  /** Whether the session is currently valid */
  valid: boolean;
  /** The active account, if any */
  account: AccountInfo | null;
  /** The refreshed access token when the session was extended */
  accessToken: string | null;
  /** Human-readable reason when session is invalid */
  reason?: string;
}

/**
 * Validate the current MSAL session.
 *
 * 1. Checks that an active (or cached) account exists.
 * 2. Attempts a silent token acquisition to prove the session is alive and
 *    to extend it (MSAL refreshes the refresh-token automatically).
 * 3. Returns a result describing whether the session is valid.
 *
 * This is a **pure function** (no side-effects like logout). Consumers
 * decide what to do with the result.
 */
export async function validateSession(
  msalInstance: IPublicClientApplication,
  scopes: string[],
): Promise<SessionValidationResult> {
  if (isMaxSessionDurationExceeded()) {
    return {
      valid: false,
      account: null,
      accessToken: null,
      reason: 'max_session_duration_exceeded',
    };
  }

  // 1. Resolve the active account
  let account = msalInstance.getActiveAccount();
  if (!account) {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      account = accounts[0];
      msalInstance.setActiveAccount(account);
    }
  }

  if (!account) {
    return { valid: false, account: null, accessToken: null, reason: 'no_account' };
  }

  // 2. Try silent token acquisition (also refreshes the session)
  try {
    const response = await msalInstance.acquireTokenSilent({ scopes, account });
    recordSessionStart();
    return { valid: true, account, accessToken: response.accessToken };
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      return { valid: false, account, accessToken: null, reason: 'interaction_required' };
    }
    return { valid: false, account, accessToken: null, reason: 'token_acquisition_failed' };
  }
}

/**
 * Convenience wrapper: validate & extend the session, returning the new token
 * or `null` if the session could not be extended.
 */
export async function extendSession(
  msalInstance: IPublicClientApplication,
  scopes: string[],
): Promise<string | null> {
  const result = await validateSession(msalInstance, scopes);
  return result.accessToken;
}

/**
 * Handle an expired / invalid session by clearing local state and
 * performing an MSAL logout (account removal from cache).
 *
 * @returns Promise that resolves after cleanup is complete.
 */
export async function handleSessionExpiry(
  msalInstance: IPublicClientApplication,
): Promise<void> {
  clearSessionStart();

  // Clean up local storage auth artefacts
  localStorage.removeItem('user');
  localStorage.removeItem('token');

  // Clear the active account pointer
  msalInstance.setActiveAccount(null);

  // Clear all cached MSAL accounts so downstream checks
  // (e.g. getAllAccounts(), msalLoggedIn) see an empty state.
  // This avoids infinite retry loops where code still believes
  // the user is logged in via a stale MSAL cache.
  try {
    await msalInstance.clearCache();
  } catch {
    // clearCache may not be available in older MSAL versions – best-effort.
  }
}
